import { css } from '@emotion/react'
import { memo, useCallback, useRef } from 'react'
import { openMenuDialog } from '../../../../actions/menu'
import { setFullScreenExpandedState } from '../../../../state/modals'
import {
    openTipsDrawer,
    openTopicDrawer,
} from '../../../../state/modals/state.drawer'
import { TransitionTypes } from '../../../../utilities/transitions'
import ButtonTipText from '../../../Buttons/ButtonTipText'
import IconButton from '../../../Buttons/IconButton'
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState'
import { useLoungeAutoplay } from '../../../Hooks/useLoungeAutoplay'
import { useFloatingRoomStateSelector } from '../../../Hooks/useModal'
import { useModelColor } from '../../../Hooks/useModelColor'
import { usePinnedRoomCount } from '../../../Hooks/useRoom'
import { useRoomSelector } from '../../../Hooks/useRoomAppState'
import { useScreenStateSelector } from '../../../Hooks/useScreenState'
import { useUserSelector } from '../../../Hooks/useUser'
import ArrowCollapseLeft from '../../../Icons/ArrowCollapseLeft'
import ArrowExpandRight from '../../../Icons/ArrowExpandRight'
import BadgeAvatar from '../../../common/BadgeAvatar'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, { maxRoomWidth } from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import shape from '../../../styles/shape'
import { COMPONENT_HEIGHTS } from '../../../theme'

const rootStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    position: 'relative',
    zIndex: 1,
    height: COMPONENT_HEIGHTS.chatHeader,
    minHeight: COMPONENT_HEIGHTS.chatHeader,
    maxHeight: COMPONENT_HEIGHTS.chatHeader,
    padding: `0 ${getThemeSpacing(1)}`,
})

const avatarContainerStyles = css({
    marginRight: getThemeSpacing(0.25),
    marginLeft: -4,
    transform: 'scale(0.75)',
})

const infoContainerStyles = css({
    width: 'calc(100% - 52px)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    zIndex: 1,
})

const nameStyles = css({
    fontSize: 13,
    margin: 0,
    fontWeight: 700,
})

const nowViewingStyles = css({
    fontSize: 13,
    fontWeight: 500,
    margin: 0,
    marginLeft: getThemeSpacing(0.5),
    letterSpacing: '0.00938em',
    cursor: 'pointer',
    [maxRoomWidth(374)]: {
        fontSize: 10.375,
    },
})

const previewStyles = css({
    borderTopLeftRadius: shape.borderRadius,
    borderTopRightRadius: shape.borderRadius,
    background: palette.background,
})

const topicTypographyStyles = css({
    fontSize: 11.375,
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
    margin: 0,
    cursor: 'pointer',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: palette.contrastText,
    [maxRoomWidth(APP_BREAKPOINTS.md)]: {
        fontSize: 10.375,
        fontWeight: 400,
    },
})

const titleStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    '& a': {
        right: 0,
        position: 'absolute',
    },
    '& button': {
        marginLeft: getThemeSpacing(1),
        transform: 'scale(0.8)',
        [maxRoomWidth(APP_BREAKPOINTS.sm)]: {
            marginLeft: 0,
        },
    },
})

const roomExpandStyles = css({
    padding: 4,
})

interface LoungeChatHeaderProps {
    id: number
    pinned?: boolean
    preview?: boolean
}

const LoungeChatHeader = memo((props: LoungeChatHeaderProps) => {
    const { id, pinned, preview } = props
    const loungeId = useUserSelector(id, 'id')
    const loungeName = useUserSelector(id, 'name')
    const loungeSurrogateID = useRoomSelector(loungeId, 'loungeSurrogateID')
    const roomExpanded = useFloatingRoomStateSelector('expanded')
    const pinnedRoomCount = usePinnedRoomCount()
    const appPortrait = useScreenStateSelector('portrait')
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')
    const slug = useUserSelector(loungeSurrogateID, 'slug')
    const name = useUserSelector(loungeSurrogateID, 'name')
    const topic = useUserSelector(loungeSurrogateID, 'topic')
    const color = useUserSelector(loungeSurrogateID, 'color')

    const modelColor = useModelColor(color)

    const headerRef = useRef<HTMLDivElement>(undefined)

    const limited =
        (preview || pinned) &&
        (headerRef.current?.clientWidth ?? 0) < APP_BREAKPOINTS.s

    const onOpenMenu = useCallback(() => {
        openMenuDialog(slug, {
            room: true,
            transitionSource: TransitionTypes.ROOM,
        })
    }, [slug])

    const onOpenTip = useCallback(() => {
        openTipsDrawer({
            slug,
            hiddenShows: true,
            chatError: true,
            pinned,
        })
    }, [slug, pinned])
    const hasSpace =
        (!pinned && !preview) || (pinned && !isMobile) || (preview && !isMobile) //!preview || (preview && !isMobile)

    useLoungeAutoplay(id)

    const onOpenTopic = useCallback(() => openTopicDrawer(slug), [slug])

    return (
        <div
            id={`chat-header-${id}`}
            className="chat-header"
            css={[rootStyles, preview && previewStyles]}
            ref={headerRef}
        >
            <div
                css={avatarContainerStyles}
                data-testid="Container__ModelAvatar"
                onClick={onOpenMenu}
            >
                <BadgeAvatar
                    disableBadge
                    broadcasting
                    disableDrag // this header isnt used anywhere dragging would make sense
                    alt={name}
                    size={40}
                    id={loungeSurrogateID}
                />
            </div>
            <div css={infoContainerStyles}>
                <div css={titleStyles}>
                    {hasSpace && !limited && (
                        <h2
                            css={nameStyles}
                            data-testid="Typography__ModelName"
                        >
                            {loungeName}
                        </h2>
                    )}
                    {!!slug && (
                        <p
                            // component="p"
                            css={nowViewingStyles}
                            data-testid="Typography__NowViewingModel"
                            onClick={onOpenMenu}
                        >
                            Now viewing{' '}
                            <span style={{ color: modelColor }}>{name}</span>
                        </p>
                    )}
                    {/** show the short name:
                     * 1) we're pinned or preview and limited
                     */}
                    {/* {!hasSpace && limited && !!slug && (
                        <Typography
                            component="p"
                            css={nowViewingStyles}
                            data-testid="Typography__NowViewingModel"
                            onClick={onOpenMenu}
                        >
                            {'> '}
                            <span style={{ color: modelColor }}>{name}</span>
                        </Typography>
                    )} */}
                    {/** show the button:
                     * 1) we're not pinned and not preview
                     * 2} we're pinned and not isMobile
                     * 3) we're preview and not isMobile
                     */}
                    {hasSpace && !limited && !isMobile && (
                        <ButtonTipText onClick={onOpenTip} />
                    )}
                    {!pinned &&
                        !preview &&
                        !isMobile &&
                        pinnedRoomCount > 0 &&
                        !appPortrait && (
                            <IconButton
                                title={`${
                                    roomExpanded ? 'Show' : 'Hide'
                                } Pinned Rooms`}
                                onClick={() =>
                                    setFullScreenExpandedState(!roomExpanded)
                                }
                                css={roomExpandStyles}
                            >
                                {roomExpanded ? (
                                    <ArrowCollapseLeft />
                                ) : (
                                    <ArrowExpandRight />
                                )}
                            </IconButton>
                        )}
                </div>
                {hasSpace && (
                    <p
                        data-testid="Typography__Topic"
                        onClick={slug ? onOpenTopic : null}
                        css={topicTypographyStyles}
                    >
                        {slug ? topic : 'Click Next Model to start video.'}
                    </p>
                )}
            </div>
        </div>
    )
})

export default LoungeChatHeader
